// Helpers locales simples
const getSafeInt = (val) => {
    if (!val) return 0;
    const cleanVal = String(val).replace(/\D/g, ''); 
    return parseInt(cleanVal || '0', 10);
};

// Busca el valor de una columna ignorando mayúsculas, minúsculas y espacios.
const getValue = (row, possibleNames) => {
    const names = Array.isArray(possibleNames) ? possibleNames : [possibleNames];
    const rowKeys = Object.keys(row);
    
    for (const name of names) {
        const normalizedToFind = name.toLowerCase().trim();
        const foundKey = rowKeys.find(k => k.toLowerCase().trim() === normalizedToFind);
        if (foundKey !== undefined) return row[foundKey];
    }
    return undefined;
};

/**
 * MercantilStrategy - Lógica de importación específica para Mercantil Andina.
 * Formato esperado de póliza en Excel: 516265019-0006-01
 * Formato en Base de Datos de la Póliza: "16265019" (String)
 */
export const MercantilStrategy = {
    name: "MERCANTIL",

    /**
     * cleanSearchValue: Extrae el número de póliza base para encontrar el documento padre en Firestore.
     * Ejemplo: "516265019-0006-01" -> "16265019"
     */
    cleanSearchValue: (rawVal) => {
        if (!rawVal) return '';
        const rawString = String(rawVal).trim();
        const parts = rawString.split('-');
        if (parts.length > 0) {
            // Quitamos el primer dígito (usualmente un '5') y devolvemos el resto del primer bloque
            return parts[0].substring(1);
        }
        return rawString;
    },

    /**
     * findExistingRecord: BUSQUEDA PROFESIONAL EN MEMORIA.
     * Para Mercantil, el match de unicidad en la cobranza se hace por el campo 'endoso'.
     */
    findExistingRecord: (row, existingRecords) => {
        if (!existingRecords || existingRecords.length === 0) return null;

        const polizaRaw = getValue(row, ['nPoliza', 'Poliza', 'Nro Poliza', 'Comprobante']) || '';
        if (!polizaRaw) return null;

        const parts = String(polizaRaw).split('-');
        // Extraemos el segundo bloque y lo convertimos a número (ej: 0006 -> 6)
        const endosoExcel = parts[1] ? parseInt(parts[1], 10) : 0;

        // Buscamos en el listado de la DB que nos pasó el orquestador
        return existingRecords.find(rec => 
            getSafeInt(rec.endoso) === endosoExcel
        ) || null;
    },

    processRow: (row, existingRecord) => {
        const polizaRaw = getValue(row, ['nPoliza', 'Poliza', 'Nro Poliza']) || '';
        const nPolizaLimpia = MercantilStrategy.cleanSearchValue(polizaRaw);
        
        // Calculamos el endoso para guardarlo en el documento de cobranza
        const parts = String(polizaRaw).split('-');
        const endoso = parts[1] ? parseInt(parts[1], 10) : 0;

        // Limpieza de Importe
        let importeExcel = 0;
        const rawImporte = getValue(row, ['Importe', 'Monto', 'Total', 'Valor']);
        if (rawImporte !== undefined && rawImporte !== null && rawImporte !== '') {
            if (typeof rawImporte === 'number') {
                importeExcel = rawImporte;
            } else {
                const cleanValue = String(rawImporte)
                    .replace(/\$/g, '').replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
                importeExcel = parseFloat(cleanValue);
            }
        }

        const moneda = getValue(row, ['Moneda', 'Divisa']) || '$';
        const formaPagoExcel = getValue(row, ['FormaPago', 'Forma de Pago', 'Medio']) || 'TARJETA';
        const motivoExcel = getValue(row, ['Motivo', 'Desc. Motivo', 'Estado']) || '';

        // Lógica de Intentos (Idempotencia)
        const excelIntento = getSafeInt(getValue(row, ['IntentoCobro', 'Intento']));
        const intentoFinal = excelIntento > 0 
            ? excelIntento 
            : (existingRecord ? (getSafeInt(existingRecord.intentoCobro) + 1) : 1);

        let estadoGestion = '';
        let estadoDeuda = '';
        let motivoDeuda = motivoExcel;

        if (existingRecord && getSafeInt(existingRecord.intentoCobro) === intentoFinal) {
            estadoGestion = existingRecord.estado || 'Pendiente';
            estadoDeuda = existingRecord.estadoDeuda || 'Rechazo';
            if (!motivoDeuda) motivoDeuda = existingRecord.motivoDeuda;
        } else {
            const formaPagoUpper = String(formaPagoExcel).toUpperCase();
            if (formaPagoUpper.includes('CBU')) {
                estadoGestion = intentoFinal === 1 ? 'Sin Asignar' : 'Pendiente';
                estadoDeuda = intentoFinal === 1 ? 'Intento de Cobro' : 'Rechazo';
            } else {
                estadoGestion = intentoFinal === 1 ? 'Sin Asignar' : 'Pendiente';
                estadoDeuda = 'Rechazo';
            }
        }

        return {
            action: 'SAVE',
            data: {
                nPoliza: String(nPolizaLimpia),
                endoso: endoso, 
                intentoCobro: intentoFinal,
                motivoDeuda: motivoDeuda,
                importe: importeExcel,
                moneda: moneda,
                estadoDeuda: estadoDeuda,
                estado: estadoGestion,
                formaPago: formaPagoExcel,
                ultimaImportacion: new Date().toISOString(),
            }
        };
    }
};